import axios from 'axios';
import Querystring from 'querystring';
import actions from 'redux/actions';
import {
  FORMAT_NUMBER_FREE_URL,
  USER_SERVLET_URL,
  EXPORT_CONTACT_URL,
  IMPORT_CONTACT_GROUP_URL,
} from 'consts/api';
import Timer from 'helpers/setTimer';
import { handleBodyRequestCommon, isDefine, IsJsonString } from 'helpers';
import { isDevelopment, isLocalhost, SecurityConst } from 'consts/data';
import { newDate } from 'helpers/date-time';
import middlewareProcess from 'middlewares/middlewareProcess';
import { isString } from 'lodash';
import SlackService from './SlackService';
import CheckBalanceModel from 'models/check-balance.model';
import UserServices from './UserServices';
import CacheService from './CacheService';
import { SystemKeys } from 'consts/cachedKey';
import { AppRoutes } from 'consts/routesPath';
import get from 'lodash/get';

const { logout } = actions;

const timer = new Timer();
const timerSendError = new Timer();

class Services {
  constructor() {
    // axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
    this.axios = axios.create({
      baseURL: isLocalhost ? '/dev' : '',
    });
    this.interceptors = null;
    this.axios.defaults.withCredentials = true;
    this.source = axios.CancelToken.source();
    this.sessionId = null;
    this.expired = false;
    this.detectedBalanceNoxtEnough = false;
    this.shouldCheckOriginEmail = false;
    this.detectedBalanceNotEnough = false;
  }

  static reportToSlack({ error, username, body }) {
    timerSendError.debounce(() => {
      SlackService.sendError({
        title: `${error?.message || ''} - ${
          error?.config?.url || ''
        } - account: ${username} - env: ${process.env.NODE_ENV} - version: ${sessionStorage.getItem(
          'version'
        )}`,
        message: JSON.stringify(body),
      });
    }, 200);
  }

  setupInterceptors(store) {
    this.axios.interceptors.request.use(
      (config) => {
        //* Calculate duration of response
        config.metadata = { startTime: newDate() };
        const { jwt } = UserServices.getJWTAuth() || {};
        config.headers.sessionId = jwt;
        this.setCookie(`sessionId=${jwt}`);

        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    //* Process when unauthorize
    this.axios.interceptors.response.use(
      (response) => {
        //* Calculate duration of response
        response.config.metadata.endTime = newDate();
        response.duration = response.config.metadata.endTime - response.config.metadata.startTime;

        middlewareProcess(response, store);

        //* Specific case - If audio generate > 120 case
        if (response?.config?.url.includes('create-spintax')) {
          if ([413].includes(response?.data?.code)) {
            return response;
          }
        }

        if (response?.data?.code >= 400 && response?.data?.code <= 500) {
          return Promise.reject(response?.data?.message || 'Error!');
        }

        return response;
      },
      async (error) => {
        //* Calculate duration of response
        const { status } = error?.response || {};
        error.config.metadata.endTime = newDate();
        error.duration = error.config.metadata.endTime - error.config.metadata.startTime;
        const pathAPI = error?.config?.url || '';

        const isLoginURL = pathAPI.includes('/login');
        const isResetPasswordAPI = pathAPI.includes('reset-password');
        const isLackOfBillingInformation = status === 412;
        const isNotEnoughCredits = status === 402;

        const username = store?.getState()?.userServletReducer?.data?.username || '';

        const bodyMessage = {
          response: error?.response?.data || '',
          requestBodyAPI: error?.response?.config?.data || '',
        };

        if (isLackOfBillingInformation) {
          CacheService.set(SystemKeys.IS_NOT_HAVE_BILLING_INFO, true);
          return Promise.reject({ ignoreShowMessage: true });
        }

        //* In case account has not enough credits
        if (isNotEnoughCredits) {
          if (this.detectedBalanceNotEnough) {
            return Promise.reject({ ignoreShowMessage: true });
          }

          CheckBalanceModel.setIsNotEnoughCredits(true);
          this.detectedBalanceNotEnough = true;
        }

        if (status === 429) {
          timer.debounce(() => {
            window.location.href = '/429';
          }, 200);
        }

        if (status === 401) {
          //* In case user need reset password
          const errorParsed =
            (IsJsonString(get(error, 'response.data.body')) &&
              JSON.parse(get(error, 'response.data.body'))) ||
            {};

          const jwtTokenForReset = get(errorParsed, 'jwtToken');
          const shouldResetPassword = !!jwtTokenForReset;
          if (shouldResetPassword && !isLoginURL) {
            UserServices.clearUserCached();
            window.location.href = `${AppRoutes.ResetPassword}?token=${jwtTokenForReset}&required=true`;
            return Promise.reject(error);
          }

          if (!this.expired && !isLoginURL) {
            this.expired = true;
            //* Log apis that return 401
            Services.reportToSlack({
              error,
              username,
              body: bodyMessage,
            });

            //* Dispatch logout
            store && store.dispatch(logout({ forceClearAll: true }));

            //* Reload
            UserServices.clearUserCached();
            window.location.reload();

            return Promise.reject('Token expired!');
          }
        }

        if (status === 400 && !isDevelopment) {
          if (!isLoginURL) {
            //* For privacy of user -> hide password that been changed
            if (isResetPasswordAPI) {
              if (isDefine(bodyMessage?.requestBodyAPI?.password)) {
                bodyMessage.requestBodyAPI.password = '<system hided>';
              }
            }

            Services.reportToSlack({
              error,
              username,
              body: bodyMessage,
            });
          }
        }

        return Promise.reject(error);
      }
    );
  }

  getLocalStorage() {
    const data = window.localStorage.getItem(SecurityConst.authStorage);

    if (typeof data === 'object' && data === null) {
      return {};
    }

    return JSON.parse(data);
  }

  decryptToSessionId(obj) {
    const { name, role, token } = obj;
    if (name && role && token) {
      return `${name}(=+${role}(=+${token}`;
    }
    return '';
  }

  attactTokenStripeToHeader(token) {
    this.interceptors = this.axios.interceptors.request.use(
      function (config) {
        // Do something before request is sent
        config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }

  attachTokenToHeader(token) {
    this.sessionId = token;
    this.interceptors = this.axios.interceptors.request.use(
      function (config) {
        // Do something before request is sent
        config.headers.sessionId = token;
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }

  removeInterceptors() {
    this.axios.interceptors.request.eject(this.interceptors);
  }

  sourceToken() {
    return this.source.token;
  }

  get(...arg) {
    return this.axios.get(...arg);
  }

  post(url, body, config) {
    return this.axios.post(url, handleBodyRequestCommon(body), config);
  }

  delete(url, config) {
    return this.axios.delete(url, config);
  }

  put(url, body, config) {
    return this.axios.put(url, handleBodyRequestCommon(body), config);
  }

  formatNumberFree({ countryCode = '', number = '' }) {
    return this.axios.post(
      FORMAT_NUMBER_FREE_URL,
      Querystring.stringify({
        number,
        countryCode,
      })
    );
  }

  testConnectionSlack({ slackURL }) {
    return this.axios.post(
      USER_SERVLET_URL,
      Querystring.stringify({
        action: 'testSlackConnection',
        slackURL,
      })
    );
  }

  getListBackup() {
    return this.axios.post(
      USER_SERVLET_URL,
      Querystring.stringify({
        action: 'olderBKFiles',
      })
    );
  }

  importContactGroups(file = null) {
    const formData = new FormData();
    formData.append('files[]', file);
    return this.axios.post(
      `${IMPORT_CONTACT_GROUP_URL}?action=restoreLocalFile&isRestoreContact=true&isRestoreData=true&isRestoreDelete=false`,
      formData
    );
  }

  getExportContactGroups(groupIds = '') {
    return this.axios.post(`${EXPORT_CONTACT_URL}?action=group&groupIds=${groupIds}`, {
      responseType: 'blob',
    });
  }

  getLinkDownloadBackup(fileName = '') {
    return this.axios.post(`${EXPORT_CONTACT_URL}?action=exportBackupFile&name=${fileName}`);
  }

  getLinkExportStats() {
    return this.axios.post(`${EXPORT_CONTACT_URL}?action=stats`, {
      responseType: 'blob',
    });
  }

  setCookie(cookie = '') {
    document.cookie = cookie;
  }

  getCookie(nameField) {
    let name = nameField + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return '';
  }
}

export const HeaderUTF8Option = {
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
  },
};
export const ResponseTypeBlob = {
  responseType: 'blob',
};
export default new Services();
